import { readLocalJsonOptional, storageKey, writeLocalJson } from "@authe/fe-commons/storage";
import type { StorageIdentity } from "@authe/fe-commons/storage";
import { recordRumEvent } from "./observability";

type Action = "catalog_created" | "investigation_created";
interface ActivationState { startedAt: number; firstAction?: Action }
export interface ActivationTracker {
  entered(): void;
  succeeded(action: Action): void;
}

/** This is first observed activation in this browser/identity, not lifetime usage. */
export function createActivationTracker(identity: StorageIdentity,
  emit = recordRumEvent, now = Date.now): ActivationTracker {
  const parts = [identity.customerId, identity.orgId, identity.tenantId, identity.userId];
  const key = parts.every(Boolean) ? storageKey("dashboard", "activation-v1", ...parts.map(p => encodeURIComponent(p!))) : null;
  let memory: ActivationState | undefined;
  const read = () => {
    const value = key ? readLocalJsonOptional<ActivationState>(key) : undefined;
    return value && Number.isFinite(value.startedAt) && value.startedAt <= now() ? value : memory;
  };
  const write = (state: ActivationState) => { memory = state; if (key) writeLocalJson(key, state); };
  const entered = () => {
    if (!key || read()) return;
    write({ startedAt: now() });
    emit("onboarding_dashboard_entered", { cohort: "first_observed_in_browser" });
  };
  return {
    entered,
    succeeded(action) {
      if (!key) return;
      entered();
      const state = read();
      if (!state || state.firstAction) return;
      write({ ...state, firstAction: action });
      emit("onboarding_first_useful_action", {
        action, elapsed_ms: Math.max(0, now() - state.startedAt), cohort: "first_observed_in_browser",
      });
    },
  };
}

/** Count only an acknowledged mutation; a click, pre-existing row or rejected API is not activation. */
export async function trackActivationResult<T>(operation: () => Promise<T>, tracker: ActivationTracker, action: Action): Promise<T> {
  const result = await operation();
  if (result != null) tracker.succeeded(action);
  return result;
}
